import * as React from "react";
import { Divider, Subtitle2, Text, makeStyles, tokens } from "@fluentui/react-components";
import { useSelectedMailItems } from "../hooks/useSelectedMailItems";
import { useSharePoint } from "../hooks/useSharePoint";
import { useUploadJobs } from "../hooks/useUploadJobs";
import { SelectedEmailPanel } from "./SelectedEmailPanel";
import { RecentJobs } from "./SharePoint/RecentJobs";
import { UploadDestination } from "./SharePoint/UploadDestination";
import { UploadOptions } from "./SharePoint/UploadOptions";
import { TaskpaneSummary } from "./TaskpaneSummary";

interface AppProps {
  title: string;
}

const useStyles = makeStyles({
  root: {
    backgroundColor: tokens.colorNeutralBackground1,
    boxSizing: "border-box",
    minHeight: "100vh",
    padding: `${tokens.spacingVerticalS} 15px`,
  },

  container: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
    margin: "0 auto",
    maxWidth: "800px",
    width: "100%",
  },

  section: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
  },

  sectionHeader: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  },

  description: {
    color: tokens.colorNeutralForeground3,
  },

  divider: {
    margin: 0,
  },
});

interface SectionProps {
  title: string;
  description: string;
  children: React.ReactNode;
}

const Section: React.FC<SectionProps> = ({ title, description, children }) => {
  const styles = useStyles();

  return (
    <section className={styles.section}>
      <div className={styles.sectionHeader}>
        <Subtitle2 as="h2">{title}</Subtitle2>
        <Text size={200} className={styles.description}>
          {description}
        </Text>
      </div>
      {children}
    </section>
  );
};

const App: React.FC<AppProps> = () => {
  const styles = useStyles();
  const sp = useSharePoint();
  const selectedEmails = useSelectedMailItems();
  const uploadJobs = useUploadJobs(sp.lastCreatedJobId);

  return (
    <div className={styles.root}>
      <div className={styles.container}>
        <TaskpaneSummary
          selectedEmailCount={selectedEmails.length}
          destinationPlatform={sp.destinationPlatform}
          selectedSite={sp.selectedSite}
          selectedDrive={sp.selectedDrive}
          selectedFolder={sp.selectedFolder}
        />

        <Divider className={styles.divider} />

        <Section
          title="Selected Emails"
          description="Confirm the emails that will be included in the upload job."
        >
          <SelectedEmailPanel items={selectedEmails} />
        </Section>

        <Divider className={styles.divider} />

        <Section
          title="Destination"
          description="Choose where the selected emails should be saved."
        >
          <UploadDestination
            destinationPlatform={sp.destinationPlatform}
            onSelectDestinationPlatform={sp.selectDestinationPlatform}
            sites={sp.sites}
            sitesLoading={sp.sitesLoading}
            sitesError={sp.sitesError}
            selectedSite={sp.selectedSite}
            onLoadSites={sp.loadSites}
            onLoadOneDriveFolders={sp.loadOneDriveFolders}
            onSelectSite={sp.selectSite}
            drives={sp.drives}
            drivesLoading={sp.drivesLoading}
            drivesError={sp.drivesError}
            selectedDrive={sp.selectedDrive}
            onSelectDrive={sp.selectDrive}
            folders={sp.folders}
            foldersLoading={sp.foldersLoading}
            foldersError={sp.foldersError}
            selectedFolder={sp.selectedFolder}
            expandedFolders={sp.expandedFolders}
            loadingFolderIds={sp.loadingFolderIds}
            onSelectFolder={sp.selectFolder}
            onToggleFolder={sp.toggleFolder}
            onExpandFolder={sp.expandFolder}
          />
        </Section>

        <Divider className={styles.divider} />

        <Section
          title="Upload"
          description="Start the background upload job when the selection and folder are ready."
        >
          <UploadOptions
            selectedFolderName={sp.selectedFolder?.name}
            selectedEmails={selectedEmails}
            upload={sp.upload}
            onUpload={sp.uploadEmail}
            onClearStatus={sp.clearUploadStatus}
          />
        </Section>

        <Divider className={styles.divider} />

        <Section
          title="Active / Recent Jobs"
          description="Follow progress, review failures, and retry failed uploads."
        >
          <RecentJobs
            jobs={uploadJobs.jobs}
            itemsByJobId={uploadJobs.itemsByJobId}
            expandedJobIds={uploadJobs.expandedJobIds}
            loading={uploadJobs.loading}
            error={uploadJobs.error}
            onRefresh={uploadJobs.refreshJobs}
            onToggleJob={uploadJobs.toggleJob}
            onRetryJob={uploadJobs.retryJob}
            onCancelJob={uploadJobs.cancelJob}
          />
        </Section>
      </div>
    </div>
  );
};

export default App;
